import { useQuery } from "@tanstack/react-query";
import React from "react";
import { useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import BookingTable from "../../../components/bookingTable/BookingTable";
import GroupMentoringCards from "../../../components/mentorGroups/GroupMentoringCards/GroupMentoringCards";
import SecondaryLoader from "../../../components/SecondaryLoader/SecondaryLoader";
import WelcomeCard from "../../../components/WelcomeCard";
import dummyImage from "../../../images/background/dummyProfilebg.png";
import { DateTime } from "luxon";
import {
  getUserCohorts
} from "../../../services/adminService";
import { getMenteesAppointments } from "../../../services/dashboardService";

const MyAppointmentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const userId = useSelector((state) => state.auth.id);

  const {
    data: appointmentsData,
    isLoading,
    error: appointmentsError,
  } = useQuery({
    queryKey: ["appointments", userId],
    queryFn: () => getMenteesAppointments(userId),
    enabled: !!userId, // Only fetch if userId exists
  });

  const handleViewRecipt = (appointment) => {
    navigate("/dashboard/mentors/appointments/view-recipt", {
      state: { appointment }, // Send appointment data via state
    });
  };

  const headers = [
    "IMAGE",
    "MENTOR NAME",
    "APPOINTMENT DATE",
    "START TIME",
    "TAGS",
  ];
  const data =
    appointmentsData?.meetings?.map((item) => {
      // ✔️ Parse the date-only field (for the calendar date)
      const appointmentDate = new Date(item.date);

      // ✔️ Parse the UTC startTime field on THIS item
      const startDateTime = new Date(item.startTime);

      // ✔️ Format just the local time portion
      const localTime12 = startDateTime.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }); // e.g. "8:00 PM"

      // ✔️ Format the calendar date
      const formattedDate = appointmentDate.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      }); // e.g. "Apr 29, 2025"

      const statusTag =
        item.appointmentId?.status === 2
          ? "CANCELLED"
          : item.appointmentId?.status === 1
          ? "COMPLETED"
          : item.appointmentId?.status === 0 && appointmentDate > new Date()
          ? "UP COMING"
          : "CANCELLED";

      return {
        IMAGE: item.mentorId.profileImage || dummyImage,
        "MENTOR NAME": `${item.mentorId.firstName} ${item.mentorId.lastName}`,
        "APPOINTMENT DATE": formattedDate,
        "START TIME": localTime12,
        TAGS: statusTag,
        details: item,
      };
    }) || [];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { year: "numeric", month: "long", day: "numeric" };
    return date.toLocaleDateString("en-US", options);
  };

  const formatFrequency = (frequency) => {
    return frequency === "weekly" ? "Weekly" : "Biweekly";
  };

  const {
    data: fetchUserCohorts,
    isLoading: fetchCUserohortsPending,
    error: fetchUserCohortsError,
  } = useQuery({
    queryKey: ["userCohorts", userId],
    queryFn: () => getUserCohorts(userId),
    enabled: !!userId,
  });

  const mentoringPrograms = fetchUserCohorts?.length
  ? fetchUserCohorts.map((program) => {
      const start = DateTime.fromISO(program.startDate, { zone: "utc" }).setZone(program.timezone);
      const end = DateTime.fromISO(program.endDate, { zone: "utc" }).setZone(program.timezone);

      return {
        id: program._id,
        description: program.description,
        mentorName: program.mentor.name,
        startDate: start.toFormat("MMMM d, yyyy"),
        endDate: end.toFormat("MMMM d, yyyy"),
        dateTime: `${start.toFormat("cccc")}, ${start.toFormat("hh:mm a")} - ${end.toFormat("hh:mm a")}`,
        timezone: program.timezone,
        frequency: formatFrequency(program.frequency),
        price: program.pricePerMentee,
        seatsAvailable: program.seatsAvailable,
        totalSeats: program.seatsAvailable,
        registeredUsers: program.registeredUsers,
        targetUsers: program.targetUsers,
        whatUserWillGetOutOfThis: program.whatUserWillGetOutOfThis
      };
    })
  : [];

  return (
    <div className="flex flex-col gap-6">
      <WelcomeCard title={"My Appointments"} />
      {/* <GroupMentoringCards mentoringPrograms={mentoringPrograms} /> */}
      {location.pathname === "/dashboard/mentors/appointments" &&
        (isLoading ? (
          <SecondaryLoader />
        ) : appointmentsError ? (
          <div className="text-red-500 text-center p-4">
            Error: {appointmentsError || "Failed to load appointments."}
          </div>
        ) : (
          <>
            <BookingTable
              headers={headers}
              data={data}
              handleViewRecipt={(appointment) => handleViewRecipt(appointment)}
            />
          </>
        ))}
      <Outlet />
    </div>
  );
};

export default MyAppointmentPage;
